import React, { useState, useEffect } from "react";
import { Alert, Box, Grid, TextField, Typography, Button, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import Switch from '@mui/material/Switch';
import FormGroup from "@mui/material/FormGroup";
import successIcon from '../assets/payment-success-icon.png';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../context/MemberProfileContext';
import parse from 'html-react-parser';
import PaypalZelle from "../components/PaypalZelle";
import { fetchPublicEventTypes } from '../api/guestRegistrationService';

const amber = '#B8860A';
const navy = '#1A3A6B';
const pageBg = '#f8f7f2';

const Section = ({ title, children }) => (
    <Box sx={{ border: '1px solid #c8d4de', borderRadius: 1.5, mb: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#e4eaf0', borderBottom: '1px solid #c8d4de', px: 3, py: 1.5 }}>
            <Typography sx={{ color: navy, fontWeight: 700, fontSize: '1rem', fontFamily: '"Roboto","Helvetica","Arial",sans-serif', textAlign: 'left' }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
            {children}
        </Box>
    </Box>
);

export default function BanquetForm() {
    const [eventData, setEventData] = useState({
        balaviharYear: "",
        membershipType: { name: "" },
        status: "",
        eventType: '',
        notes: "first event1",
        children: [],
        dayCare: [],
        medical: {
            pcpName: "",
            pcpMobile: "",
            medicalInsurance: "",
            groupNo: "",
            idNo: "",
            medicalNotes: "",
            medicalConsentSignedBy: "",
            treatmentConsentSignedBy: ""
        },
        transaction: {
            quantities: 1,
            itemPrice: 0,
            totalAmount: "",
            donationAmount: "",
            payerId: "",
            notes: "Multiple Events",
        },
        events: [],
        feeEnabled: 0,
        parent: {
            firstName: "",
            lastName: "",
            email: "",
            mobileNumber: ""
        }
    });

    const { accessToken, signOut } = useAuth();
    const navigate = useNavigate();
    const { memberships, setMemberships, memberProfile, eventTypes } = useMember();
    const API_URL = process.env.REACT_APP_API_URL;
    const isGuest = !accessToken;

    const [ticketPrice, setTicketPrice] = useState(0);
    const [eventTypeId, setEventTypeId] = useState(0);
    const [error, setError] = useState(null);
    const [rulesAccepted, setRulesAccepted] = useState(false);
    const [feeEnabled, setFeeEnabled] = useState(0);
    const [donation, setDonation] = useState(0);
    const [noOfPeople, setNoOfPeople] = useState(1);
    const [totalAmount, setTotalAmount] = useState(0);
    const [banquetEventType, setBanquetEventType] = useState(0);
    const [memBvRegdReqd, setMemBvRegdReqd] = useState();
    const [addChild, setAddChild] = useState(false);
    const [childFields, setChildFields] = useState([]);
    const [medicalFields, setMedicalFields] = useState(eventData.medical);
    const [inputFields, setInputFields] = useState(eventData.transaction);
    const [payloadData, setPayloadData] = useState({});
    const [success, setSuccess] = useState(false);
    const [checkout, setCheckout] = useState(false);
    const [email, setEmail] = useState("");
    const [guestInfo, setGuestInfo] = useState({ firstName: '', lastName: '', email: '', mobileNumber: '' });
    const [guestErrors, setGuestErrors] = useState({});

    useEffect(() => {
        if (accessToken) {
            memberProfileFetch();
        }
        // Guests stay on the page — no redirect
    }, [accessToken]);

    // Load event data from the public endpoint for guests
    useEffect(() => {
        if (!accessToken) {
            fetchPublicEventTypes().then((events) => {
                const banqEvent = events.find((e) => e.name === 'Banquet');
                if (banqEvent) {
                    setBanquetEventType(banqEvent);
                    setTicketPrice(banqEvent.price);
                    setEventTypeId(banqEvent.eventTypeId);
                    setMemBvRegdReqd(banqEvent.memBvRegdReqd);
                    setEventData((prev) => ({
                        ...prev,
                        balaviharYear: banqEvent.eventYear,
                        eventType: banqEvent.name,
                        events: [{ memberId: null, eventTypeId: banqEvent.eventTypeId, quantities: noOfPeople }],
                    }));
                }
            }).catch(() => {});
        }
    }, []);

    useEffect(() => {
        if (memberProfile.primary) {
            setEmail(memberProfile.primary.email);
            eventData.parent.firstName = memberProfile.primary.firstName;
            eventData.parent.lastName = memberProfile.primary.lastName;
            eventData.parent.email = memberProfile.primary.email;
            eventData.parent.mobileNumber = memberProfile.primary.mobileNumber?.replace(/[-\s]/g, '') ?? '';
        }
    }, []);

    useEffect(() => {
        if (eventTypes && eventTypes.length > 0) {
            const banqEventType = eventTypes.find((et) => et.name === 'Banquet');
            setBanquetEventType(banqEventType);
            if (banqEventType) {
                setTicketPrice(banqEventType.price);
                setEventTypeId(banqEventType.eventTypeId);
                setMemBvRegdReqd(banqEventType.memBvRegdReqd);
                setEventData((prev) => ({
                    ...prev,
                    balaviharYear: banqEventType.eventYear,
                    eventType: banqEventType.name,
                    events: [{
                        memberId: memberProfile.primary?.memberId,
                        eventTypeId: banqEventType.eventTypeId,
                        quantities: noOfPeople,
                    }],
                }));
            }
        }
    }, [eventTypes, noOfPeople, memberProfile.primary?.memberId]);

    useEffect(() => {
        eventData.transaction.itemPrice = ticketPrice;
        eventData.transaction.quantities = noOfPeople;
        if (feeEnabled) {
            setTotalAmount(((parseInt(noOfPeople) * parseInt(ticketPrice) + parseInt(donation)) * 1.03).toFixed(2));
        } else {
            setTotalAmount((parseInt(noOfPeople) * parseInt(ticketPrice) + parseInt(donation)) * 1.00);
        }
        eventData.transaction.totalAmount = totalAmount;
    }, [noOfPeople, donation, feeEnabled, ticketPrice, eventData.events]);

    const total = () => {
        inputFields.donationAmount = donation;
        inputFields.totalAmount = totalAmount;
        eventData.transaction = inputFields;
    };
    total();

    const handleLogout = () => { signOut(); navigate('/'); };

    const memberProfileFetch = async () => {
        try {
            const response = await fetch(`${API_URL}/gquery`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-cmdfw-jwt-token': accessToken },
                body: JSON.stringify({
                    query: `
                        query {
                          memberships {
                              balaviharYear
                              balaviharPeriod
                              status
                              totalAmountPaid
                              membershipTypes {
                                  membershipTypeId
                                  name
                                  startDate
                                  endDate
                                  bvPrice
                                  childQuantities
                              }
                              availableSessions {
                                  sessionId
                                  center
                                  sessionName
                                  grades {
                                      gradeId
                                      grade
                                  }
                              }
                              registeredSessions {
                                  bvRegistrationId
                                  regStartDate
                                  regEndDate
                                  status
                                  memberEntity { firstName lastName fullName }
                                  sessionEntity { sessionId center sessionName }
                                  gradeEntity { gradeId grade }
                              }
                              currentMembership {
                                  membershipId
                                  startDate
                                  endDate
                                  status
                                  notes
                                  bvYear
                                  createdAt
                                  createdBy
                                  membershipTypesEntity { membershipTypeId name startDate endDate }
                                  transactionEntity {
                                      transactionId
                                      eventTypesEntity { eventTypeId name eventYear }
                                      totalAmount
                                      paymentStatus
                                      createdAt
                                      createdBy
                                  }
                              }
                          }
                        }
                    `,
                }),
            });
            const data = await response.json();
            if (response.ok) {
                setMemberships(data.data.memberships);
            } else if (response.status === 403) {
                handleLogout();
            }
        } catch (err) {
            console.error('Error fetching membership profile:', err);
            signOut();
        }
    };

    const validateGuestInfo = () => {
        const errs = {};
        if (!guestInfo.firstName.trim()) errs.firstName = 'Required';
        if (!guestInfo.lastName.trim()) errs.lastName = 'Required';
        if (!guestInfo.email.trim() || !/\S+@\S+\.\S+/.test(guestInfo.email)) errs.email = 'Valid email required';
        if (!guestInfo.mobileNumber.trim()) errs.mobileNumber = 'Required';
        setGuestErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const checkoutHandler = (e) => {
        e.preventDefault();
        setError(null);

        if (isGuest) {
            if (!validateGuestInfo()) return;
            eventData.parent.firstName = guestInfo.firstName;
            eventData.parent.lastName = guestInfo.lastName;
            eventData.parent.email = guestInfo.email;
            eventData.parent.mobileNumber = guestInfo.mobileNumber;

            if (totalAmount > 0) {
                eventData.status = "pending";
                setPayloadData({ ...eventData });
                setCheckout(true);
            } else {
                eventData.status = "paid";
                eventData.transaction.paymentStatus = "free";
                const guestPayload = {
                    firstName: guestInfo.firstName,
                    lastName: guestInfo.lastName,
                    email: guestInfo.email,
                    phone: guestInfo.mobileNumber,
                    eventTypeId: eventTypeId,
                    numTickets: noOfPeople,
                    customFields: null,
                    transaction: {
                        itemPrice: eventData.transaction.itemPrice,
                        donationAmount: 0,
                        totalAmount: 0,
                        paymentStatus: 'free',
                        payerId: '',
                        paymentType: 'Free',
                    },
                };
                fetch(`${API_URL}/guestRegistration`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(guestPayload),
                }).then((res) => {
                    if (res.ok) setSuccess(true);
                    else res.json().then((d) => setError(d.message || 'Registration failed'));
                }).catch(() => setError('Registration failed. Please try again.'));
                setCheckout(true);
            }
            return;
        }

        // Authenticated flow
        if (totalAmount > 0) {
            eventData.status = "pending";
        } else {
            eventData.status = "paid";
            setSuccess(true);
        }
        setPayloadData(eventData);
        sendToServer();
    };

    async function sendToServer() {
        const maxRetries = 3;
        let attempt = 0;
        while (attempt < maxRetries) {
            try {
                const response = await fetch(`${API_URL}/events`, {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json', 'x-cmdfw-jwt-token': accessToken },
                    body: JSON.stringify(eventData),
                });
                if (response.ok) { setCheckout(true); return true; }
                if (response.status === 403) {
                    const d = await response.json();
                    handleLogout();
                    throw new Error(d.message || 'OTP expired');
                }
                const d = await response.json();
                setError(d.message);
                return false;
            } catch (err) {
                attempt++;
                if (attempt >= maxRetries) {
                    setCheckout(false);
                    setError("Failed to send data to the server. Please try again later.");
                    return false;
                }
            }
        }
    }

    function handlePeople(e) { setNoOfPeople(parseInt(e.target.value)); }
    function feeSwitchHandler(e) { setError(null); setFeeEnabled(e.target.checked); eventData.feeEnabled = e.target.checked; }
    function checkOutEnableHandler(e) { setError(null); setRulesAccepted(e.target.checked); }
    function handleBack() { setCheckout(false); }
    function handleDonation(e) {
        setInputFields({ ...inputFields, [e.target.name]: parseInt(e.target.value) });
        if (e.target.value >= 0) setDonation(parseInt(e.target.value));
    }
    const handleCancel = () => navigate(isGuest ? '/' : '/EventTypesForm');

    function childCareSwitchHandler(e) {
        setAddChild(e.target.checked);
        eventData.parent.childCareReqd = e.target.checked;
        setChildFields(e.target.checked ? [{ firstName: "", lastName: "", age: 0 }] : []);
        eventData.dayCare = [];
    }
    const handleAddFields = () => setChildFields([...childFields, { firstName: "", lastName: "", age: 0 }]);
    const handleRemoveFields = (index) => {
        const values = [...childFields];
        values.splice(index, 1);
        setChildFields(values);
        eventData.dayCare = values;
    };
    const handleChildChange = (e, index) => {
        const newForm = [...childFields];
        newForm[index][e.target.name] = e.target.value;
        setChildFields(newForm);
        eventData.dayCare = newForm;
    };
    function handleMedicalChange(e) {
        const newForm = { ...medicalFields, [e.target.name]: e.target.value };
        setMedicalFields(newForm);
        eventData.medical = newForm;
    }

    return (
        <Box sx={{ bgcolor: pageBg, minHeight: '100vh', py: 4, px: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: 760, bgcolor: '#fff', borderRadius: 2, boxShadow: 3, overflow: 'hidden', alignSelf: 'flex-start' }}>

                {/* Page header */}
                <Box sx={{ bgcolor: navy, px: { xs: 3, md: 5 }, py: 3 }}>
                    <Typography variant="h5" sx={{ color: '#fff', fontFamily: 'Georgia, serif', fontWeight: 700, letterSpacing: 0.5 }}>
                        Banquet
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#c5d8ea', mt: 0.5 }}>
                        CMDFW Event Registration
                    </Typography>
                </Box>

                <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
                    {checkout ? (
                        success ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <img src={successIcon} className="Success-icon" alt="success" />
                                <Typography variant="h5" sx={{ color: navy, fontFamily: 'Georgia, serif', mt: 2, mb: 1 }}>
                                    Thank You for Registering!
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#555' }}>
                                    A confirmation email will be sent to <b>{isGuest ? guestInfo.email : email}</b>
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <PaypalZelle eventData={eventData} setSuccess={setSuccess} payloadData={payloadData} isGuest={isGuest} eventTypeId={eventTypeId} />
                                <Button variant="outlined" sx={{ mt: 3, borderColor: '#aaa', color: '#555' }} onClick={handleBack}>
                                    Back
                                </Button>
                            </Box>
                        )
                    ) : (
                        <form onSubmit={checkoutHandler}>

                            {/* Event Details */}
                            <Section title="Event Details">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: navy, mb: 0.5 }}>
                                    {banquetEventType.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#555' }}>
                                    {parse(banquetEventType.desc || "")}
                                </Typography>
                            </Section>

                            {/* Membership status */}
                            {memberships.status === "ACTIVE" && (
                                <Box sx={{ bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 1, p: 2, mb: 3 }}>
                                    <Typography variant="body2">
                                        Membership: <b>Active</b> — {memberships.balaviharPeriod}
                                    </Typography>
                                    <Typography variant="body2">
                                        Type: <b>{memberships.currentMembership.membershipTypesEntity.name}</b>
                                    </Typography>
                                </Box>
                            )}

                            {memBvRegdReqd && memberships.status !== "ACTIVE" && (
                                <Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 1, p: 2, mb: 3 }}>
                                    <Typography variant="body2" sx={{ color: '#bf360c', fontWeight: 700, mb: 0.5 }}>
                                        Membership expired
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                                        Please renew your membership for <b>{memberships.balaviharPeriod}</b> before registering.
                                    </Typography>
                                    <Button onClick={handleCancel} variant="outlined" size="small" sx={{ borderColor: amber, color: amber }}>
                                        Cancel
                                    </Button>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#777' }}>
                                        Questions? <a href="mailto:membership@cmdfw.org" style={{ color: amber }}>membership@cmdfw.org</a>
                                    </Typography>
                                </Box>
                            )}

                            {((memBvRegdReqd && memberships.status === "ACTIVE") || !memBvRegdReqd) && (
                                <>
                                    {/* Tickets */}
                                    <Section title="Number of Tickets">
                                        <TextField
                                            fullWidth
                                            label="Number of Tickets"
                                            type="number"
                                            inputProps={{ min: 1, max: 20 }}
                                            name="quantities"
                                            variant="outlined"
                                            value={noOfPeople}
                                            onChange={handlePeople}
                                        />
                                    </Section>

                                    {/* Your Information (guest only) */}
                                    {isGuest && (
                                        <Section title="Your Information">
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth required label="First Name"
                                                        value={guestInfo.firstName}
                                                        onChange={(e) => setGuestInfo({ ...guestInfo, firstName: e.target.value })}
                                                        error={!!guestErrors.firstName} helperText={guestErrors.firstName} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth required label="Last Name"
                                                        value={guestInfo.lastName}
                                                        onChange={(e) => setGuestInfo({ ...guestInfo, lastName: e.target.value })}
                                                        error={!!guestErrors.lastName} helperText={guestErrors.lastName} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth required label="Email Address" type="email"
                                                        value={guestInfo.email}
                                                        onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                                                        error={!!guestErrors.email} helperText={guestErrors.email} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth type="number" required label="Mobile"
                                                        value={guestInfo.mobileNumber}
                                                        onChange={(e) => setGuestInfo({ ...guestInfo, mobileNumber: e.target.value })}
                                                        error={!!guestErrors.mobileNumber} helperText={guestErrors.mobileNumber} />
                                                </Grid>
                                            </Grid>
                                        </Section>
                                    )}

                                    {/* Child Care */}
                                    <Section title="Child Care (Optional)">
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={addChild}
                                                        onChange={childCareSwitchHandler}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': { color: amber },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: amber },
                                                        }}
                                                    />
                                                }
                                                label="Need Child Care"
                                            />
                                        </FormGroup>

                                        {addChild && (
                                            <Box sx={{ mt: 2 }}>
                                                {childFields.map((childField, index) => (
                                                    <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField fullWidth label="Child First Name" name="firstName" variant="outlined"
                                                                value={childField.firstName} onChange={(e) => handleChildChange(e, index)} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField fullWidth label="Child Last Name" name="lastName" variant="outlined"
                                                                value={childField.lastName} onChange={(e) => handleChildChange(e, index)} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={3}>
                                                            <TextField fullWidth label="Age" name="age" type="number" variant="outlined"
                                                                inputProps={{ min: 1, max: 12 }}
                                                                value={childField.age} onChange={(e) => handleChildChange(e, index)} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <IconButton onClick={() => handleRemoveFields(index)}><DeleteIcon /></IconButton>
                                                            <IconButton onClick={handleAddFields}><PersonAddIcon /></IconButton>
                                                        </Grid>
                                                    </Grid>
                                                ))}

                                                <Box sx={{ bgcolor: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 1, p: 2, mt: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Release Authorization</Typography>
                                                    <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
                                                        RELEASE OF LIABILITY — In consideration of Chinmaya Mission granting the above named child permission to
                                                        participate in the event activities, I hereby assume all risks of her/his personal injury that may result
                                                        from any event activity. As parent/guardian/responsible party, I do indemnify, defend and hold harmless,
                                                        Chinmaya Mission and its officers, Volunteer parents and all participants in the event program from and
                                                        against all liability, including claims and suits at law or in equity, for injury, fatal or otherwise,
                                                        which may result from any negligence and/or the child taking part in activities.
                                                    </Typography>
                                                    <TextField
                                                        fullWidth required
                                                        label="Signature (Type your Name)"
                                                        name="medicalConsentSignedBy"
                                                        variant="outlined"
                                                        value={medicalFields.medicalConsentSignedBy}
                                                        onChange={handleMedicalChange}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Section>

                                    {/* Payment Summary */}
                                    <Section title="Payment Summary">
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Additional Donation ($)"
                                                    type="number"
                                                    name="donation"
                                                    variant="outlined"
                                                    value={donation}
                                                    onChange={handleDonation}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox checked={feeEnabled} onChange={feeSwitchHandler} name="feeEnabled"
                                                            sx={{ '&.Mui-checked': { color: amber } }} />
                                                    }
                                                    label="Add 3% to cover credit card processing fees"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth disabled
                                                    id="totalAmountField"
                                                    label="Total Amount ($)"
                                                    type="number"
                                                    variant="outlined"
                                                    inputProps={{ style: { fontSize: 22, fontWeight: 700, color: navy } }}
                                                    InputLabelProps={{ style: { fontSize: 16 } }}
                                                    value={totalAmount}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Section>

                                    {/* Waiver */}
                                    <Section title="Waiver">
                                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                                            Please read the waiver before proceeding:{' '}
                                            <a href="https://membership.cmdfw.org/docs/cmdfw-waiver.pdf" target="_blank" rel="noopener noreferrer" style={{ color: amber }}>
                                                Open PDF
                                            </a>
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox checked={rulesAccepted} onChange={checkOutEnableHandler} name="rulesAccepted"
                                                    sx={{ '&.Mui-checked': { color: amber } }} />
                                            }
                                            label="I have read and agree to the rules and policies"
                                        />
                                    </Section>

                                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                                    {/* Action buttons */}
                                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                        <Button onClick={handleCancel} variant="outlined" sx={{ borderColor: '#bbb', color: '#555', px: 3 }}>
                                            Cancel
                                        </Button>
                                        <Tooltip title={!rulesAccepted ? "Please accept the rules and policies" : ""}>
                                            <span>
                                                <Button type="submit" variant="contained" disabled={!rulesAccepted}
                                                    sx={{
                                                        bgcolor: amber, color: '#fff', fontWeight: 700, px: 4,
                                                        '&:hover': { bgcolor: '#9a6f08' },
                                                        '&.Mui-disabled': { bgcolor: '#e8d5a0', color: '#fff' },
                                                    }}>
                                                    Proceed to Checkout
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Box>

                                    <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#999' }}>
                                        Questions? Contact us at{' '}
                                        <a href="mailto:membership@cmdfw.org" style={{ color: amber }}>membership@cmdfw.org</a>
                                    </Typography>
                                </>
                            )}
                        </form>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
